import { Box, Typography, Stack } from '@mui/material';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import { PointhomeFunnelParticipationData } from '../../../../types/pointhomeFunnel.types';
import PointhomeParticipationTable from './PointhomeParticipationTable';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';

type Props = {
  data: PointhomeFunnelParticipationData;
  selectedApp?: string | null;
  startDate: Dayjs;
  endDate: Dayjs;
  selectedContentFilter?: string;
};

export default function PointhomeParticipationDashboard({
  data,
  selectedApp,
  startDate,
  endDate,
  selectedContentFilter,
}: Props) {
  if (!data || data.length === 0) return null;

  // 룰렛 필터인지 확인
  const isRouletteFilter = selectedContentFilter === '룰렛';
  const isTicketFilter = selectedContentFilter === '티켓';

  // 날짜 오름차순 정렬 (API가 내림차순일 수 있으므로)
  const sortedData = [...data].sort((a, b) => dayjs(a.date).diff(dayjs(b.date)));

  // 룰렛 필터인 경우 날짜별로 그룹화하여 전체 룰렛 데이터 합산
  let chartData: Array<{
    date: string;
    fullDate: string;
    content: string;
    dau: number;
    participationRate: number;
  }>;

  let totalDau: number;
  let totalParticipated: number;
  let avgDau: number;
  let avgRate: number;
  let contentName: string;

  if (isRouletteFilter || isTicketFilter) {
    // 날짜별로 그룹화하여 데이터 합산 (중복 제거)
    const groupedByDate = new Map<string, {
      dau: number;
      participatedUser: number;
      ticketUserCount: number;
    }>();

    sortedData.forEach((item) => {
      const date = item.date;
      if (!groupedByDate.has(date)) {
        groupedByDate.set(date, {
          dau: item.dau,
          participatedUser: 0,
          ticketUserCount: 0
        });
      }
      const dateData = groupedByDate.get(date);
      if (!dateData) return;
      
      dateData.participatedUser += item.participatedUser;
      dateData.ticketUserCount += item.ticketUserCount ?? 0;
    });

    // 차트용 데이터 매핑 (날짜별 합산). 티켓: 참여율 = 티켓적립유저/DAU, 룰렛: 참여율 = 클릭유저/DAU
    chartData = Array.from(groupedByDate.entries())
      .sort((a, b) => dayjs(a[0]).diff(dayjs(b[0])))
      .map(([date, data]) => ({
        date: dayjs(date).format('MM/DD'),
        fullDate: date,
        content: isRouletteFilter ? '룰렛' : '티켓',
        dau: data.dau,
        participationRate: data.dau > 0
          ? ((isTicketFilter ? data.ticketUserCount : data.participatedUser) / data.dau) * 100
          : 0,
      }));

    // 요약 통계 계산 (날짜별 합산)
    totalDau = Array.from(groupedByDate.values()).reduce((sum, item) => sum + item.dau, 0);
    totalParticipated = Array.from(groupedByDate.values()).reduce(
      (sum, item) => sum + (isTicketFilter ? item.ticketUserCount : item.participatedUser),
      0
    );
    avgDau = Math.round(totalDau / groupedByDate.size);
    avgRate = totalDau > 0 ? (totalParticipated / totalDau) * 100 : 0;
    contentName = isRouletteFilter ? '룰렛' : '티켓';
  } else {
    // 일반 필터: 날짜별로 그룹화하여 중복 제거
    const groupedByDate = new Map<string, {
      dau: number;
      participatedUser: number;
      content: string;
    }>();

    sortedData.forEach((item) => {
      const date = item.date;
      if (!groupedByDate.has(date)) {
        groupedByDate.set(date, {
          dau: item.dau,
          participatedUser: item.participatedUser,
          content: item.content
        });
      } else {
        // 같은 날짜가 있으면 참여 유저 수 합산
        const dateData = groupedByDate.get(date);
        if (dateData) {
          dateData.participatedUser += item.participatedUser;
        }
      }
    });

    // 차트용 데이터 매핑 (날짜별로 하나만)
    chartData = Array.from(groupedByDate.entries())
      .sort((a, b) => dayjs(a[0]).diff(dayjs(b[0])))
      .map(([date, data]) => ({
        date: dayjs(date).format('MM/DD'),
        fullDate: date,
        content: data.content,
        dau: data.dau,
        participationRate: data.dau > 0 ? (data.participatedUser / data.dau) * 100 : 0,
      }));

    // 요약 통계 계산
    totalDau = Array.from(groupedByDate.values()).reduce((sum, item) => sum + item.dau, 0);
    totalParticipated = Array.from(groupedByDate.values()).reduce((sum, item) => sum + item.participatedUser, 0);
    avgDau = Math.round(totalDau / groupedByDate.size);
    avgRate = totalDau > 0 ? (totalParticipated / totalDau) * 100 : 0;
    contentName = sortedData[0]?.content || '';
  }

  return (
    <Box sx={{ width: '100%', mt: 3 }}>
      {/* 상단 요약 카드 영역 */}
      <Stack direction="row" spacing={2} mb={3}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            border: '1px solid #e0e0e0',
            borderRadius: 2,
            overflow: 'hidden',
            height: 48,
          }}
        >
          <Box
            sx={{
              backgroundColor: '#4dabf7', // 파란색 배경
              color: '#fff',
              px: 2,
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              fontWeight: 600,
              fontSize: 14,
            }}
          >
            평균 DAU
          </Box>
          <Box
            sx={{
              backgroundColor: '#fff',
              px: 2,
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              fontWeight: 700,
              fontSize: 16,
            }}
          >
            {avgDau.toLocaleString()}
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            border: '1px solid #e0e0e0',
            borderRadius: 2,
            overflow: 'hidden',
            height: 48,
          }}
        >
          <Box
            sx={{
              backgroundColor: '#ff6b6b', // 붉은색 배경
              color: '#fff',
              px: 2,
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              fontWeight: 600,
              fontSize: 14,
            }}
          >
            평균 참여율
          </Box>
          <Box
            sx={{
              backgroundColor: '#fff',
              px: 2,
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              fontWeight: 700,
              fontSize: 16,
            }}
          >
            {avgRate.toFixed(1)}%
          </Box>
        </Box>
      </Stack>

      {/* 차트 영역 */}
      <Box
        sx={{
          backgroundColor: '#ffffff',
          border: '1px solid #e0e0e0',
          borderRadius: 3,
          p: 3,
        }}
      >
        <Typography variant="h6" align="center" sx={{ mb: 4, fontWeight: 700 }}>
          {contentName} 참여율 추이
        </Typography>

        <ResponsiveContainer width="100%" height={200}>
          <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="colorDau" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#4dabf7" stopOpacity={0.1}/>
                <stop offset="95%" stopColor="#4dabf7" stopOpacity={0}/>
              </linearGradient>
              <linearGradient id="colorRate" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#ff6b6b" stopOpacity={0.1}/>
                <stop offset="95%" stopColor="#ff6b6b" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e0e0e0" />
            <XAxis 
              dataKey="date" 
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#666', fontSize: 12 }}
              dy={10}
            />
            {/* 왼쪽 Y축: 참여율 (%) */}
            <YAxis
              yAxisId="left"
              orientation="left"
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#666', fontSize: 12 }}
              tickFormatter={(value) => `${value}%`}
              domain={[0, 'auto']} // 참여율은 100% 넘을 수 없지만 데이터에 따라 auto
            />
            {/* 오른쪽 Y축: DAU */}
            <YAxis
              yAxisId="right"
              orientation="right"
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#666', fontSize: 12 }}
              tickFormatter={(value) => value >= 1000 ? `${(value / 1000).toFixed(0)}k` : value}
            />
            <Tooltip
              contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
              formatter={(value: number, name: string) => [
                name === '참여율' ? `${value.toFixed(2)}%` : value.toLocaleString(),
                name
              ]}
              labelFormatter={(label) => `${label}`}
            />
            {/* DAU 영역 (오른쪽 축) - 파란색 */}
            <Area
              yAxisId="right"
              type="monotone"
              dataKey="dau"
              name="DAU"
              stroke="#4dabf7"
              fillOpacity={1}
              fill="url(#colorDau)"
              strokeWidth={2}
              dot={{ r: 3, fill: '#4dabf7', strokeWidth: 0 }}
              activeDot={{ r: 5 }}
            />
            {/* 참여율 영역 (왼쪽 축) - 붉은색 */}
            <Area
              yAxisId="left"
              type="monotone"
              dataKey="participationRate"
              name="참여율"
              stroke="#ff6b6b"
              fillOpacity={1}
              fill="url(#colorRate)"
              strokeWidth={2}
              dot={{ r: 3, fill: '#ff6b6b', strokeWidth: 0 }}
              activeDot={{ r: 5 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </Box>

      <Box sx={{ mt: 3 }}>
        <PointhomeParticipationTable 
          data={sortedData} 
          selectedApp={selectedApp}
          startDate={startDate}
          endDate={endDate}
          selectedContentFilter={selectedContentFilter}
        />
      </Box>
    </Box>
  );
}
